import { memo, useCallback, useMemo, useRef, useState, ReactElement } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Space, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps, notification } from '@ohpen/enso-ui';
import { Guard } from '@ohpen/react-authz';
import { lockActionTitle, changeRequestNotificationMessage } from '@pmp/utils';
import { ActionEntityType } from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';

import { ProcurationFeeAssignmentsListProps, GenericChangeResponse } from '../interfaces';

const { Text } = Typography;

export const ProcurationFeeAssignmentsListBase = <T extends { lockedByActionId?: string }, U extends GenericChangeResponse<any>>({
  procurationFees,
  loading,
  valueLists,
  readonly,
  parentEntityType,
  columns,
  onCreate,
  onView,
  onEdit,
  onDelete,
  refreshTable,
  enableRowDnD,
  onRowDnDEnd,
  guardFlags = [],
  permissionKey,
  lockedKey = 'lockedByActionId',
}: ProcurationFeeAssignmentsListProps<T, U>): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [editOrder, setEditOrder] = useState(false);
  const [order, setOrder] = useState<T[] | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const isOrderLocked = useMemo(() => procurationFees?.some(item => !!item[lockedKey]), [procurationFees, lockedKey]);
  const orderLockedByActionId = useMemo(
    () => (isOrderLocked ? (procurationFees?.find(x => x[lockedKey])?.[lockedKey] as string) : ''),
    [procurationFees, isOrderLocked, lockedKey],
  );

  const onApplyOrder = useCallback(() => setShowConfirmModal(true), []);
  const handleConfirmApply = useCallback(() => {
    if (!order) return;
    if (onRowDnDEnd)
      onRowDnDEnd(order)
        .then(({ data: { isValid } }: { data: U }) => {
          if (!isValid) {
            notification().error({ message: t('global.errors.unexpected') });
          }
          refreshTable();
          notification().success({
            message: changeRequestNotificationMessage(t, enableFourEyes),
          });
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
        })
        .finally(() => {
          setShowConfirmModal(false);
          setEditOrder(false);
          setConfirmLoading(false);
        });
  }, [order, onRowDnDEnd, refreshTable, t, enableFourEyes]);

  const handleCancelApply = useCallback(() => setShowConfirmModal(false), []);

  const getRowActions: (record: T) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="view" title={t('global.buttons.view')} placement="top">
          <Button type="text" aria-label="View Procuration Fee" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={[`${permissionKey}:update`]} flags={guardFlags}>
          <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Edit Procuration Fee"
              icon={<EditOutlined key="edit" />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId || readonly}
            />
          </Tooltip>
        </Guard>,
        <Guard key="delete" permissions={[`${permissionKey}:delete`]} flags={guardFlags}>
          <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Delete Procuration Fee"
              icon={<DeleteOutlined key="delete" />}
              onClick={() => onDelete(record)}
              disabled={!!record.lockedByActionId || readonly}
            />
          </Tooltip>
        </Guard>,
      ];
      return rowActions;
    },
    [onDelete, onEdit, onView, readonly, t, guardFlags, permissionKey],
  );

  const tableColumns = useMemo(() => columns, [columns]);

  const tableProps: TableProps<T> = useMemo(
    () => ({
      id: `${parentEntityType}ProcurationFees`,
      itemsLabel:
        parentEntityType === ActionEntityType.LENDER_FIRMS
          ? t('lender.firms.labels.procurationFee')
          : t('lender.lender.labels.procurationFee'),
      dataSource: procurationFees ?? [],
      columns: tableColumns,
      rowKey: 'id',
      valueLists,
      loading,
      ref: tableRef,
      getRowActions: getRowActions,
      enableRowDnD: editOrder && !!enableRowDnD,
      onRowDnDEnd: ({ newOrder }: { newOrder: T[] }) => setOrder(newOrder),
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
    }),
    [procurationFees, tableColumns, valueLists, loading, getRowActions, editOrder, enableRowDnD, parentEntityType, t],
  );

  return (
    <>
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              {editOrder ? (
                <>
                  <Button onClick={() => setEditOrder(false)}>{t('global.buttons.cancel')}</Button>
                  <Button color="primary" variant="solid" icon={<CheckOutlined />} onClick={onApplyOrder}>
                    {t('global.buttons.apply')}
                  </Button>
                </>
              ) : (
                <>
                  <Table.ResetFiltersButton />
                  <Table.SelectColumnsButton />
                  <Guard permissions={[`${permissionKey}:update`]} flags={guardFlags}>
                    {enableRowDnD && (
                      <Tooltip key="edit" title={lockActionTitle('edit', orderLockedByActionId)} placement="top">
                        <Button
                          color="primary"
                          icon={<EditOutlined />}
                          onClick={() => {
                            tableRef.current?.resetFilters();
                            setEditOrder(true);
                          }}
                          disabled={isOrderLocked || readonly}
                        >
                          {t('global.buttons.editOrder')}
                        </Button>
                      </Tooltip>
                    )}
                  </Guard>
                  <Guard permissions={[`${permissionKey}:create`]} flags={guardFlags}>
                    {onCreate && <Table.CreateButton onClick={onCreate} disabled={readonly} />}
                  </Guard>
                </>
              )}
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
      <Modal
        open={showConfirmModal}
        title={t('lender.lender.labels.applyOrder')}
        onOk={handleConfirmApply}
        onCancel={handleCancelApply}
        okText={t('global.buttons.apply')}
        cancelText={t('global.buttons.cancel')}
        confirmLoading={confirmLoading}
      >
        <Text>{t('lender.lender.labels.confirmApplyFeesOrder')}</Text>
      </Modal>
    </>
  );
};

export const ProcurationFeeAssignmentsList = memo(ProcurationFeeAssignmentsListBase) as <T, U>(
  props: ProcurationFeeAssignmentsListProps<T, U>,
) => ReactElement;
